import { Box, Container } from "@mui/material";
import {
  SmartCheckbox,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { CameraItemDto } from "~/api/GeneratedApi";
import {
  AISRouterAutocompleteOption,
  AISRouterSmartAutocomplete,
} from "~/components/AISRouterSmartAutocomplete";
import { useAccess } from "~/utils/hooks/useAccess";
import { CameraViewTab } from "./CameraViewTab";

export interface CameraFormInputs {
  name: string;
  description: string;
  streamUrl: string;
  aisModem: AISRouterAutocompleteOption | null;
  record: boolean;
}

type Props = {
  onSubmit: (data: CameraFormInputs) => void;
  readOnly: boolean;
  defaultValues: CameraItemDto | null;
  formId: string;
};

function dtoToForm(d: CameraItemDto | null): CameraFormInputs {
  return {
    name: d?.name ?? "",
    aisModem: d?.aisModemDeviceId ? { deviceId: d.aisModemDeviceId } : null,
    description: d?.description ?? "",
    streamUrl: d?.streamUrl ?? "",
    record: d?.record ?? true,
  };
}

export const CameraForm = ({
  defaultValues,
  onSubmit,
  formId,
  readOnly,
}: Props): JSX.Element => {
  const { t } = useTranslation("ais-routers");
  const { CameraStream } = useAccess();

  const formDefaultValues = React.useMemo(
    () => dtoToForm(defaultValues),
    [defaultValues],
  );

  const formMethods = useSmartForm<CameraFormInputs>({
    defaultValues: formDefaultValues,
  });

  return (
    <Container sx={{ p: 0 }} maxWidth={false}>
      <SmartForm<CameraFormInputs>
        onSubmit={onSubmit}
        readOnly={readOnly}
        methods={formMethods}
        noGrid
        formId={formId}
      >
        <Box
          sx={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              xl: "1fr 1fr 1fr",
            },
          }}
        >
          <SmartTextField<CameraFormInputs>
            name="name"
            label={t("name")}
            fullWidth
            readOnly={readOnly}
            required
          />
          <SmartTextField<CameraFormInputs>
            name="description"
            label={t("description")}
            fullWidth
            readOnly={readOnly}
            required
          />
          <SmartTextField<CameraFormInputs>
            name="streamUrl"
            label={t("streamUrl")}
            fullWidth
            readOnly={readOnly}
            required
          />
          <SmartCheckbox<CameraFormInputs>
            name="record"
            label={t("record")}
            readOnly={readOnly}
          />
          <AISRouterSmartAutocomplete<CameraFormInputs>
            name="aisModem"
            label={t("aisModem")}
            fullWidth
            readOnly={readOnly}
          />
        </Box>
      </SmartForm>

      {defaultValues && CameraStream ? (
        <CameraViewTab camera={defaultValues} />
      ) : null}
    </Container>
  );
};
